import { createContext, useContext, useEffect, useState } from "react";

import { Link, useLocation } from "@remix-run/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Banner } from "~/components/ui/banner";
import { useAnonymous } from "~/hooks/use-anonymous";
import { trackEvent } from "~/lib/analytics";
import { useApiClient } from "~/hooks/use-api-client";

import {
    useActiveSurveys,
    useAnswerSurveyDeliveryPrompt,
    useDismissSurvey,
    useDismissSurveyDeliveryPrompt,
    useSaveResponse,
    useSurveyDeliveryPrompt,
} from "../hooks";
import type { ActiveSurvey, SurveyResponseState } from "../types";
import { setCachedEligibleTriggers } from "../utils/eligible-triggers-cache";
import { isGlobalSurveySuppressed } from "../utils/survey-surface-priority";
import type { EligibleTriggersData } from "../server/eligible-triggers-service";

import { SingleQuestionPopup } from "./single-question-popup";

// AIDEV-NOTE: Survey provider context for managing survey display and responses
// Handles showing CTAs, opening surveys, and tracking progress

interface SurveyContextValue {
    activeSurvey: ActiveSurvey | null;
    isShowingSurvey: boolean;
    dismissSurvey: () => void;
    startSurvey: () => void;
}

const SurveyContext = createContext<SurveyContextValue | null>(null);

export function useSurveyContext() {
    const context = useContext(SurveyContext);
    if (!context) {
        throw new Error("useSurveyContext must be used within SurveyProvider");
    }
    return context;
}

interface SurveyProviderProps {
    children: React.ReactNode;
}

export function SurveyProvider({ children }: SurveyProviderProps) {
    const { isAnonymous } = useAnonymous();
    const location = useLocation();
    const currentPath = location.pathname;
    // AIDEV-NOTE: Guided onboarding and embedded delivery surfaces own attention;
    // global surveys may resume only after the user leaves those workflows.
    const embeddedSurfaceHasPriority = isGlobalSurveySuppressed(
        currentPath,
        location.search,
    );
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    // AIDEV-NOTE: Don't show surveys to non-logged-in users (EVO-1158)
    const enabled = !isAnonymous;

    const { data: surveys, isLoading } = useActiveSurveys(
        currentPath,
        enabled && !embeddedSurfaceHasPriority,
    );
    const { data: toastDeliveryPrompt } = useSurveyDeliveryPrompt({
        surfaceKey: "toast",
        enabled: enabled && !embeddedSurfaceHasPriority,
    });
    const answerToastDelivery = useAnswerSurveyDeliveryPrompt("toast");
    const dismissToastDelivery = useDismissSurveyDeliveryPrompt("toast");
    const [isShowingSurvey, setIsShowingSurvey] = useState(false);

    // AIDEV-NOTE: Track just-completed survey ID to prevent AutoShowTrigger from re-mounting
    // immediately after completion. Without this, the cached displaySurvey data would allow
    // AutoShowTrigger to mount between when isShowingSurvey becomes false and when the
    // refetch completes with filtered data, causing the survey to reappear.
    const [justCompletedSurveyId, setJustCompletedSurveyId] = useState<
        string | null
    >(null);

    // AIDEV-NOTE: Track locally dismissed survey IDs for immediate UI feedback
    // This hides the toast/CTA immediately without waiting for the async query refetch
    const [locallyDismissedIds, setLocallyDismissedIds] = useState<Set<string>>(
        new Set(),
    );

    // AIDEV-NOTE: Soft-closed surveys/deliveries — hidden for the rest of this
    // session without firing a server dismissal. Used by the mobile modal X so
    // a quick tap doesn't permanently dismiss a survey.
    const [softClosedSurveyIds, setSoftClosedSurveyIds] = useState<Set<string>>(
        new Set(),
    );
    const [softClosedDeliveryIds, setSoftClosedDeliveryIds] = useState<
        Set<string>
    >(new Set());

    // AIDEV-NOTE: Fetch eligible triggers (prefetched in root.tsx loader)
    // Used to populate module-level cache for analytics.ts
    const { data: eligibleTriggersData } = useQuery({
        queryKey: ["surveys", "eligible-triggers"],
        enabled,
        queryFn: async () => {
            const response = await apiClient.get("/surveys/eligible-triggers");
            if (!response.ok) {
                return { triggers: [] };
            }
            return (await response.json()) as EligibleTriggersData;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
    });

    // AIDEV-NOTE: Sync eligible triggers to module-level cache for analytics.ts
    // This allows trackEvent() to check locally before making POST requests
    useEffect(() => {
        if (eligibleTriggersData?.triggers) {
            setCachedEligibleTriggers(
                eligibleTriggersData.triggers.map((t) => t.eventName),
            );
        }
    }, [eligibleTriggersData]);
    const [responseState, setResponseState] =
        useState<SurveyResponseState | null>(null);

    // AIDEV-NOTE: Preserve the active survey during navigation to prevent popup from disappearing
    // When route changes, the query refetches with a new key, causing surveys to be undefined briefly
    // This causes the popup to unmount. To fix this, we maintain our own state for the showing survey
    const [showingSurvey, setShowingSurvey] = useState<ActiveSurvey | null>(
        null,
    );

    // Get the first active survey to show (priority: first in list)
    const activeSurvey = surveys?.[0] ?? null;

    // When a survey is being shown, preserve it even during navigation/refetch
    useEffect(() => {
        if (activeSurvey && isShowingSurvey) {
            // If we're showing a survey and a new one comes in, keep showing the current one
            // This prevents the popup from disappearing during page transitions
            if (!showingSurvey || showingSurvey.id !== activeSurvey.id) {
                setShowingSurvey(activeSurvey);
            }
        } else if (!isShowingSurvey) {
            // If we're not showing a survey, update showingSurvey to match activeSurvey
            setShowingSurvey(activeSurvey);
        }
    }, [activeSurvey, isShowingSurvey, showingSurvey]);

    // Use the preserved survey when showing, otherwise use the active survey
    const displaySurvey =
        isShowingSurvey && showingSurvey ? showingSurvey : activeSurvey;

    // AIDEV-NOTE: Mutation takes surveyId as parameter to avoid stale closure issues
    const dismissMutation = useDismissSurvey();
    const saveMutation = useSaveResponse({
        surveyId: displaySurvey?.id ?? "",
        surveyTitle: displaySurvey?.title ?? "",
    });

    // AIDEV-NOTE: Listen for survey trigger checks and refetch active surveys
    // This ensures newly queued event-triggered surveys appear without page refresh
    useEffect(() => {
        const handleTriggerCheck = () => {
            queryClient.invalidateQueries({
                queryKey: ["surveys", "active"],
                exact: false,
            });
            // AIDEV-NOTE: Also refetch eligible triggers in case completion/dismissal changed eligibility
            queryClient.invalidateQueries({
                queryKey: ["surveys", "eligible-triggers"],
            });
        };

        window.addEventListener("survey-trigger-checked", handleTriggerCheck);
        return () => {
            window.removeEventListener(
                "survey-trigger-checked",
                handleTriggerCheck,
            );
        };
    }, [queryClient]);

    // Track when CTA is shown (but not for single_popup - they auto-show without CTA)
    useEffect(() => {
        if (
            displaySurvey &&
            !isLoading &&
            displaySurvey.format !== "single_popup"
        ) {
            trackEvent("survey_cta_shown", {
                surveyId: displaySurvey.id,
                surveyTitle: displaySurvey.title,
                ctaType: displaySurvey.ctaType,
                format: displaySurvey.format,
            });
        }
    }, [displaySurvey?.id, isLoading, displaySurvey?.format]);

    const dismissSurvey = () => {
        if (displaySurvey) {
            // AIDEV-NOTE: Immediately add to locally dismissed set for instant UI feedback
            // This hides toast/CTA without waiting for async query refetch
            setLocallyDismissedIds((prev) =>
                new Set(prev).add(displaySurvey.id),
            );
            // Pass surveyId at call time to avoid stale closure issues
            dismissMutation.mutate({
                surveyId: displaySurvey.id,
                surveyTitle: displaySurvey.title,
            });
        }
        setIsShowingSurvey(false);
        setResponseState(null);
    };

    const startSurvey = () => {
        if (displaySurvey) {
            setIsShowingSurvey(true);
            setResponseState({
                surveyId: displaySurvey.id,
                responses: {},
                currentQuestionIndex: 0,
                isComplete: false,
            });
            trackEvent("survey_started", {
                surveyId: displaySurvey.id,
                surveyTitle: displaySurvey.title,
                format: displaySurvey.format,
            });
        }
    };

    const handleAnswer = (questionId: string, answer: string | string[]) => {
        if (!responseState || !displaySurvey) return;

        const newResponses = {
            ...responseState.responses,
            [questionId]: answer,
        };

        // For single_popup format, completing one question completes the survey
        const isComplete = displaySurvey.format === "single_popup";

        // Get current question for analytics
        const currentQuestion =
            displaySurvey.questions[responseState.currentQuestionIndex];

        saveMutation.mutate(
            {
                responses: newResponses,
                isComplete,
                questionId: currentQuestion?.id,
                questionType: currentQuestion?.questionType,
            },
            {
                onSuccess: () => {
                    if (isComplete) {
                        // AIDEV-NOTE: Set justCompletedSurveyId BEFORE changing isShowingSurvey
                        // This prevents AutoShowTrigger from mounting for this survey
                        setJustCompletedSurveyId(displaySurvey.id);
                        setIsShowingSurvey(false);
                        setResponseState(null);

                        // Clear justCompletedSurveyId after a delay to allow refetch to complete
                        // and client-side filter to take effect
                        setTimeout(() => {
                            setJustCompletedSurveyId(null);
                        }, 5000);
                    } else {
                        // For wizard format, move to next question
                        setResponseState({
                            ...responseState,
                            responses: newResponses,
                            currentQuestionIndex:
                                responseState.currentQuestionIndex + 1,
                        });
                    }
                },
            },
        );
    };

    const handleToastDeliveryAnswer = (
        _questionId: string,
        answer: string | string[],
    ) => {
        if (!toastDeliveryPrompt) return;
        answerToastDelivery.mutate({
            prompt: toastDeliveryPrompt,
            answer,
        });
    };

    const softCloseCurrentQuestion = () => {
        if (displaySurvey) {
            setSoftClosedSurveyIds((prev) =>
                new Set(prev).add(displaySurvey.id),
            );
        }
        setIsShowingSurvey(false);
        setResponseState(null);
    };

    const softCloseToastDelivery = () => {
        if (toastDeliveryPrompt) {
            setSoftClosedDeliveryIds((prev) =>
                new Set(prev).add(toastDeliveryPrompt.deliveryId),
            );
        }
    };

    // AIDEV-NOTE: Desktop X — hide immediately for instant UI feedback, then fire
    // the server dismissal. Without the local-set update the toast lingered until
    // the refetch returned null (~500ms–1s on staging), reading as a broken X.
    const dismissToastDeliveryNow = () => {
        if (!toastDeliveryPrompt) return;
        setSoftClosedDeliveryIds((prev) =>
            new Set(prev).add(toastDeliveryPrompt.deliveryId),
        );
        dismissToastDelivery.mutate({ prompt: toastDeliveryPrompt });
    };

    const contextValue: SurveyContextValue = {
        activeSurvey: displaySurvey,
        isShowingSurvey,
        dismissSurvey,
        startSurvey,
    };

    // Get current question for single_popup format
    const currentQuestion =
        displaySurvey?.format === "single_popup" && isShowingSurvey
            ? displaySurvey.questions[responseState?.currentQuestionIndex ?? 0]
            : null;

    // Show CTA toast for surveys that haven't been started yet
    // AIDEV-NOTE: Skip CTA for single_popup - they auto-show directly
    // AIDEV-NOTE: Check locallyDismissedIds for immediate hide on dismiss click
    const showCtaToast =
        displaySurvey &&
        !embeddedSurfaceHasPriority &&
        !isShowingSurvey &&
        !isLoading &&
        !locallyDismissedIds.has(displaySurvey.id) &&
        displaySurvey.ctaType === "toast" &&
        displaySurvey.format !== "single_popup";

    // Show CTA banner for surveys that haven't been started yet
    // AIDEV-NOTE: Skip CTA for single_popup - they auto-show directly
    // AIDEV-NOTE: Check locallyDismissedIds for immediate hide on dismiss click
    const showCtaBanner =
        displaySurvey &&
        !embeddedSurfaceHasPriority &&
        !isShowingSurvey &&
        !isLoading &&
        !locallyDismissedIds.has(displaySurvey.id) &&
        displaySurvey.ctaType === "banner" &&
        displaySurvey.format !== "single_popup";

    // AIDEV-NOTE: Full toast delivery uses the wizard CTA; one_question keeps inline answering.
    const showToastDeliveryCta =
        toastDeliveryPrompt &&
        !embeddedSurfaceHasPriority &&
        toastDeliveryPrompt.deliveryMode === "full" &&
        !currentQuestion &&
        !showCtaBanner &&
        !showCtaToast &&
        !isShowingSurvey;

    // AIDEV-NOTE: Debug logging for survey display issues (disabled - logs removed)
    useEffect(() => {
        // Debug logging disabled - use browser devtools for inspection
    }, [
        activeSurvey,
        displaySurvey,
        surveys,
        isLoading,
        isShowingSurvey,
        showCtaBanner,
        showCtaToast,
        currentPath,
    ]);

    return (
        <SurveyContext.Provider value={contextValue}>
            {children}

            {/* CTA Banner - top banner inviting user to take the survey */}
            {showCtaBanner && displaySurvey && (
                <SurveyCtaBanner
                    title={displaySurvey.ctaTitle}
                    description={displaySurvey.ctaDescription}
                    surveyId={displaySurvey.id}
                    format={displaySurvey.format}
                    currentPath={currentPath}
                    onDismiss={dismissSurvey}
                />
            )}

            {/* CTA Toast - invites user to take the survey */}
            {showCtaToast && displaySurvey && (
                <SurveyCtaToast
                    title={displaySurvey.ctaTitle}
                    description={displaySurvey.ctaDescription}
                    surveyId={displaySurvey.id}
                    format={displaySurvey.format}
                    currentPath={currentPath}
                    onStart={startSurvey}
                    onDismiss={dismissSurvey}
                />
            )}

            {showToastDeliveryCta && toastDeliveryPrompt && (
                <SurveyCtaToast
                    title={toastDeliveryPrompt.survey.ctaTitle}
                    description={toastDeliveryPrompt.survey.ctaDescription}
                    surveyId={toastDeliveryPrompt.survey.id}
                    format="wizard"
                    currentPath={currentPath}
                    dismissible={
                        toastDeliveryPrompt.dismissalBehavior !== "none"
                    }
                    onDismiss={() =>
                        dismissToastDelivery.mutate({
                            prompt: toastDeliveryPrompt,
                        })
                    }
                />
            )}

            {/* Render single question popup when active */}
            {!embeddedSurfaceHasPriority &&
                currentQuestion &&
                displaySurvey && (
                    <SingleQuestionPopup
                        question={currentQuestion}
                        onAnswer={handleAnswer}
                        onDismiss={dismissSurvey}
                        onMobileClose={softCloseCurrentQuestion}
                        isSubmitting={saveMutation.isPending}
                    />
                )}

            {/* Render one-question delivery-service toast when no legacy survey prompt is active */}
            {toastDeliveryPrompt &&
                !embeddedSurfaceHasPriority &&
                toastDeliveryPrompt.deliveryMode !== "full" &&
                !softClosedDeliveryIds.has(toastDeliveryPrompt.deliveryId) &&
                !currentQuestion &&
                !showCtaBanner &&
                !showCtaToast &&
                !isShowingSurvey && (
                    <SingleQuestionPopup
                        question={toastDeliveryPrompt.question}
                        onAnswer={handleToastDeliveryAnswer}
                        onDismiss={dismissToastDeliveryNow}
                        onMobileClose={softCloseToastDelivery}
                        dismissible={
                            toastDeliveryPrompt.dismissalBehavior !== "none"
                        }
                        isSubmitting={
                            answerToastDelivery.isPending ||
                            dismissToastDelivery.isPending
                        }
                    />
                )}

            {/* Auto-show single_popup surveys */}
            {/* AIDEV-NOTE: Check justCompletedSurveyId to prevent re-showing immediately after completion */}
            {/* AIDEV-NOTE: Check locallyDismissedIds for immediate hide on dismiss click */}
            {displaySurvey?.format === "single_popup" &&
                !embeddedSurfaceHasPriority &&
                !isShowingSurvey &&
                !isLoading &&
                displaySurvey.id !== justCompletedSurveyId &&
                !locallyDismissedIds.has(displaySurvey.id) &&
                !softClosedSurveyIds.has(displaySurvey.id) && (
                    <AutoShowTrigger onShow={startSurvey} />
                )}
        </SurveyContext.Provider>
    );
}

// Component to auto-trigger showing the survey after a brief delay
function AutoShowTrigger({ onShow }: { onShow: () => void }) {
    useEffect(() => {
        // Brief delay to let the page settle
        const timer = setTimeout(onShow, 1500);
        return () => clearTimeout(timer);
    }, [onShow]);

    return null;
}

// AIDEV-NOTE: CTA Banner component shown at top to invite users to take a survey
// For wizard format, links to /survey/:surveyId route. Banners are only used with wizard format.
interface SurveyCtaBannerProps {
    title: string;
    description: string | null;
    surveyId: string;
    format: "wizard" | "single_popup";
    currentPath: string;
    onDismiss: () => void;
}

function SurveyCtaBanner({
    title,
    description,
    surveyId,
    format,
    currentPath,
    onDismiss,
}: SurveyCtaBannerProps) {
    const surveyUrl = `/survey/${surveyId}?returnTo=${encodeURIComponent(
        currentPath,
    )}`;

    const handleCtaClick = () => {
        trackEvent("survey_cta_clicked", {
            surveyId,
            surveyTitle: title,
            ctaType: "banner",
            format,
        });
    };

    return (
        <Banner
            data-testid="survey-cta-banner"
            title={title}
            description={description || undefined}
            variant="forest"
            dismissible={true}
            onDismiss={onDismiss}
        >
            {/* AIDEV-NOTE: Banners are only shown for wizard format (filtered by showCtaBanner) */}
            <Link
                to={surveyUrl}
                onClick={handleCtaClick}
                className="whitespace-nowrap rounded-md bg-white px-4 py-2 text-sm font-medium text-brand-forest-700 shadow-sm hover:bg-brand-cream-50"
            >
                Take Survey
            </Link>
        </Banner>
    );
}

// AIDEV-NOTE: CTA Toast component shown in bottom-right to invite users to take a survey
// For wizard format, links to /survey/:surveyId route. For single_popup, uses callback.
interface SurveyCtaToastProps {
    title: string;
    description: string | null;
    surveyId: string;
    format: "wizard" | "single_popup";
    currentPath: string;
    onStart?: () => void;
    onDismiss: () => void;
    dismissible?: boolean;
}

function SurveyCtaToast({
    title,
    description,
    surveyId,
    format,
    currentPath,
    onStart,
    onDismiss,
    dismissible = true,
}: SurveyCtaToastProps) {
    const surveyUrl = `/survey/${surveyId}?returnTo=${encodeURIComponent(
        currentPath,
    )}`;

    // Track CTA click for wizard format (single_popup is tracked via onStart -> survey_started)
    const handleCtaClick = () => {
        trackEvent("survey_cta_clicked", {
            surveyId,
            surveyTitle: title,
            ctaType: "toast",
            format,
        });
    };

    return (
        <div
            data-testid="survey-cta-toast"
            className="fixed bottom-4 right-4 z-50 w-80 animate-in slide-in-from-bottom-4 fade-in duration-300"
        >
            <div className="rounded-lg border bg-card p-4 shadow-lg">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                        <h4 className="font-semibold text-card-foreground">
                            {title}
                        </h4>
                        {description && (
                            <p className="mt-1 text-sm text-muted-foreground">
                                {description}
                            </p>
                        )}
                    </div>
                    {dismissible && (
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Dismiss"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="mt-3 flex gap-2">
                    {format === "wizard" ? (
                        <Link
                            to={surveyUrl}
                            onClick={handleCtaClick}
                            className="flex-1 rounded-md bg-primary px-3 py-2 text-center text-sm font-medium text-primary-foreground hover:bg-primary/90"
                        >
                            Take Survey
                        </Link>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                handleCtaClick();
                                onStart?.();
                            }}
                            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                        >
                            Take Survey
                        </button>
                    )}
                    {dismissible && (
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
                        >
                            Later
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
